// THE geometry of the unified Browser pane, in one place.
//
// Every viewer that renders <POIHierarchyBrowser> docks it in the same slide-in pane, and every
// piece of viewer chrome that must not sit under that pane offsets by the SAME numbers. Those
// numbers used to be typed literally at ~15 call sites across four files (300 / -320 / 340 /
// 400), so widening the pane meant hunting them down — and missing one produced exactly the
// overlap this module exists to prevent. Import these instead of writing a pixel value.
//
// Layout model (LTR; RTL mirrors left↔right via browserPaneSide):
//
//   |<-- WIDTH -->|  gap  |<-- viewer chrome
//   ├─────────────┤       ├─ CONTROL_INSET  = WIDTH + 40  (toolbars, FAB cluster)
//   │  Browser    │       └─ FLOATING_INSET = WIDTH + 100 (panels that open beside the FABs)
//   │  pane       │
//   └─────────────┘  CONTENT_INSET = WIDTH  (edge-to-edge surfaces, e.g. the search panel)
//
// HIDDEN_OFFSET is WIDTH + 20 so the pane's box-shadow clears the viewport when closed.

/** Pane width. The single number every other value here is derived from. */
export const BROWSER_PANE_WIDTH_PX = 322;
export const BROWSER_PANE_WIDTH = `${BROWSER_PANE_WIDTH_PX}px`;

/** Off-screen position of the closed pane (width + shadow clearance). */
export const BROWSER_PANE_HIDDEN_OFFSET = `-${BROWSER_PANE_WIDTH_PX + 20}px`;

/** Inset for viewer toolbars / FAB clusters while the pane is open. */
export const BROWSER_PANE_CONTROL_INSET = `${BROWSER_PANE_WIDTH_PX + 40}px`;

/** Inset for floating panels that open beside the FAB column (help, measurement readout). */
export const BROWSER_PANE_FLOATING_INSET = `${BROWSER_PANE_WIDTH_PX + 100}px`;

/** Inset for surfaces that span the viewer edge to edge (the bottom search panel). */
export const BROWSER_PANE_CONTENT_INSET = BROWSER_PANE_WIDTH;

/**
 * Side-aware absolute placement: the pane docks on the start edge, so LTR consumers set `left`
 * and RTL consumers set `right`. Returns both keys so a spread never leaves a stale value behind.
 */
export const browserPaneSide = (
  isRTL: boolean,
  inset: string,
): { left: string; right: string } => (
  isRTL ? { right: inset, left: 'auto' } : { left: inset, right: 'auto' }
);

/**
 * Horizontal correction for chrome centred with `left: 50%; translateX(-50%)` over the FULL
 * viewer width (the native Autodesk toolbar, the drawing-mode toolbars). The pane covers one
 * side, so centring on the whole width drifts the element under it; shifting by half the pane
 * width re-centres it in the VISIBLE area. Returns `{}` when the pane is closed.
 */
export const browserPaneCenterShift = (
  isRTL: boolean,
  isPaneOpen: boolean,
): { marginLeft?: string; marginRight?: string } => {
  if (!isPaneOpen) return {};
  const half = `${Math.round(BROWSER_PANE_WIDTH_PX / 2)}px`;
  return isRTL ? { marginRight: half } : { marginLeft: half };
};

/** Same correction as a CSS declaration, for the injected stylesheets that theme LMV chrome. */
export const browserPaneCenterShiftCss = (isRTL: boolean, isPaneOpen: boolean): string => {
  const shift = browserPaneCenterShift(isRTL, isPaneOpen);
  if (shift.marginLeft) return `margin-left: ${shift.marginLeft} !important;`;
  if (shift.marginRight) return `margin-right: ${shift.marginRight} !important;`;
  return 'margin-left: 0 !important; margin-right: 0 !important;';
};
